import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from "@mui/material";
import { login as loginAction } from "../../store/slices/authSlice";
import { useForm } from "../../hooks";
import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import MDButton from "../../components/MDButton";
import { ThemeToggleButton } from "../../components/ThemeToggle";

const AuthPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading, error, isAuthenticated } = useSelector(
    (state) => state.auth,
  );
  const { values, handleChange, resetForm } = useForm({
    username: "",
    password: "",
  });

  // Check for dev mode bypass
  const isDevMode = false;
  //import.meta.env.MODE === "development" ||
  //window.location.search.includes("dev=true");

  useEffect(() => {
    // If already authenticated or in dev mode, redirect to dashboard
    if (isAuthenticated || isDevMode) {
      console.log("Redirecting to dashboard");
      navigate("/dashboard");
    }
  }, [isAuthenticated, isDevMode, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await dispatch(loginAction(values));
      if (response.meta.requestStatus === "fulfilled") {
        resetForm();
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Login failed:", error);
    }
  };

  // Dev mode bypass
  if (isDevMode && !isAuthenticated) {
    return (
      <MDBox
        position="absolute"
        width="100%"
        minHeight="100vh"
        sx={{
          background: "linear-gradient(195deg, #42424a, #191919)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Card sx={{ maxWidth: 400, width: "100%", margin: 2 }}>
          <CardContent sx={{ p: 4 }}>
            <MDBox textAlign="center" mb={3}>
              <MDTypography
                variant="h4"
                fontWeight="medium"
                color="dark"
                mt={1}
              >
                Development Mode
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Authentication bypassed for development
              </MDTypography>
            </MDBox>
            <MDButton
              variant="contained"
              color="primary"
              fullWidth
              onClick={() => navigate("/dashboard")}
              sx={{
                background: "linear-gradient(195deg, #42424a, #191919)",
                py: 1.5,
              }}
            >
              Continue to Dashboard
            </MDButton>
          </CardContent>
        </Card>
      </MDBox>
    );
  }

  return (
    <MDBox
      position="absolute"
      width="100%"
      minHeight="100vh"
      sx={{
        background: (theme) =>
          theme.palette.mode === "dark"
            ? theme.palette.background.default
            : "url('https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=1500&q=80')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        boxShadow: "none",
        border: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Box sx={{ position: "absolute", top: 16, right: 16 }}>
        <ThemeToggleButton sx={{ color: "text.primary" }} />
      </Box>
      <Card sx={{ maxWidth: 400, width: "100%", margin: 2 }}>
        <CardContent sx={{ p: 4 }}>
          <MDBox textAlign="center" mb={3}>
            <MDTypography variant="h4" fontWeight="medium" color="dark" mt={1}>
              Sign In
            </MDTypography>
            <MDTypography variant="body2" color="text">
              Enter your username and password to sign in
            </MDTypography>
          </MDBox>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <TextField
                fullWidth
                label="Username"
                type="text"
                name="username"
                value={values.username}
                onChange={handleChange}
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0.5rem",
                  },
                }}
              />
            </MDBox>

            <MDBox mb={2}>
              <TextField
                fullWidth
                label="Password"
                type="password"
                name="password"
                value={values.password}
                onChange={handleChange}
                required
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "0.5rem",
                  },
                }}
              />
            </MDBox>

            <MDBox mt={4} mb={1}>
              <MDButton
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                disabled={loading}
                sx={{
                  background: "linear-gradient(195deg, #42424a, #191919)",
                  py: 1.5,
                  "&:hover": {
                    background: "linear-gradient(195deg, #191919, #42424a)",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Sign In"
                )}
              </MDButton>
            </MDBox>

            <MDBox mt={3} mb={1} textAlign="center">
              <MDTypography variant="button" color="text">
                Need help? Contact your administrator
              </MDTypography>
            </MDBox>
          </MDBox>
        </CardContent>
      </Card>
    </MDBox>
  );
};

export default AuthPage;
